import React, { useEffect, useState } from 'react';
import { useSEO } from '../hooks/useSEO';
import { TIMELINE, SITE } from '../data/mock';
import { Quote, Compass, Eye, Target, Crown, Star, ChevronRight } from 'lucide-react';
import { api } from '../lib/api';
import { EditRegion } from '../components/AdminEditBar';
import PageHero from '../components/PageHero';
import { InlineText, InlineImage } from '../components/InlineEditable';

// Real board of directors data — extracted from https://yekuv.org/yonetim-kurulumuz/
const CHAIRMAN = {
  name: 'Ş. Tülay ERKAN',
  role: 'Mütevelli Heyet ve Yönetim Kurulu Başkanı',
  image: '/presidents/tulay-erkan-yonetim.jpg',
  message: `Değerli Mütevellilerimiz ve Eğitim Dostlarımız; yeni bir dönem, yeni ufuklar… 33 yıldır büyük bir çaba ve özveriyle geliştirilen vakfımız, temel değerlere sadık kalarak çağın gereklerini yerine getirerek kararlılıkla yoluna devam edecektir. Çocukların ve gençlerin geleceklerine katkı sunmak önceliğimizdir; genişleyen ve olgunlaşan kurumsal hafızamız en önemli değerimizdir. Yol arkadaşlarımızla geçmişten yararlanarak geleceğe yükselerek yola devam edeceğiz.`,
  motto: 'Her çaba bir çocuk daha okusun diye…',
};

const EXECUTIVE = [
  { name: 'Necla AKBAYRAK', role: '1. Başkan Yardımcısı', short: '1. BŞK. YRD.' },
  { name: 'Göksel EKMEKÇİ', role: '2. Başkan Yardımcısı', short: '2. BŞK. YRD.' },
  { name: 'Betül YÖNEY', role: 'Sekreter', short: 'SEKRETER' },
  { name: 'Nazlı ALİBEGAKİ ABANOZ', role: 'Muhasip Üye', short: 'MUHASİP' },
];

const MEMBERS = [
  { name: 'İzzet Cem AKSOY', role: 'Üye' },
  { name: 'Mebruke YILMAZ', role: 'Üye' },
  { name: 'Emel Şebnem ZEYBEK ÖZKUT', role: 'Üye' },
  { name: 'Emine Serdar EROL', role: 'Üye' },
  { name: 'Esin ARDA', role: 'Üye' },
  { name: 'Hatice TURAÇ', role: 'Üye' },
];

const initials = (name) => name.split(' ').slice(-2).map(w => w[0]).join('').toUpperCase();

export default function About() {
  useSEO('about');
  const [chairman, setChairman] = useState(CHAIRMAN);
  const [executive, setExecutive] = useState(EXECUTIVE);
  const [members, setMembers] = useState(MEMBERS);

  useEffect(() => {
    api.publicContent('presidents').then(rows => {
      if (Array.isArray(rows) && rows.length > 0) {
        const current = rows[rows.length - 1];
        const d = current.data || {};
        setChairman(prev => ({ ...prev, name: d.name || prev.name, image: d.image || prev.image, role: d.role || prev.role, message: d.bio || prev.message, motto: d.quote || prev.motto }));
      }
    }).catch(() => {});
    api.publicContent('board_members').then(rows => {
      if (Array.isArray(rows) && rows.length > 0) {
        const exec = rows.filter(r => (r.data?.group || 'members') === 'executive').map(r => ({ name: r.data.name, role: r.data.role, short: r.data.short || (r.data.role || '').toUpperCase().slice(0, 12) }));
        const mem = rows.filter(r => (r.data?.group || 'members') === 'members').map(r => ({ name: r.data.name, role: r.data.role || 'Üye' }));
        if (exec.length) setExecutive(exec);
        if (mem.length) setMembers(mem);
      }
    }).catch(() => {});
  }, []);

  return (
    <AboutPageBody chairman={chairman} executive={executive} members={members} />
  );
}

function AboutPageBody({ chairman: CHAIRMAN, executive: EXECUTIVE, members: MEMBERS }) {
  // CHAIRMAN/EXECUTIVE/MEMBERS variables below now refer to dynamic state via shadowing
  const _CHAIRMAN = CHAIRMAN;
  const _EXECUTIVE = EXECUTIVE;
  const _MEMBERS = MEMBERS;
  return (
    <EditRegion label="Hakkımızda" to="/admin/static-pages">
      <>
      <PageHero slug="about"
        kicker="Kurumsal"
        title={<><span className="italic text-coral-200">33 yıldır</span> eğitim için<br />bir arada.</>}
        text={`${SITE.fullName} (YEKÜV), 1992'den bu yana Türkiye'nin dört bir yanındaki çocuk ve gençlerimize eğitimde fırsat eşitliği sağlamak üzere çalışmaktadır.`}
      />

      {/* Tarihçe timeline */}
      <section id="tarihce" className="bg-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <InlineText settingKey="about_history_kicker" fallback="Tarihçemiz" as="div"
                className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold" />
              <InlineText settingKey="about_history_title" fallback="Bir çeyrek asırlık yolculuk." as="h2"
                multiline className="font-display text-4xl md:text-5xl mt-4 leading-[1.05] text-ink-600" />
              <InlineText settingKey="about_history_text" fallback="Kuruluşumuzdan bugüne hayata dokunan kilometre taşlarımız." as="p"
                multiline className="mt-6 text-ink-500 leading-relaxed" />
            </div>
            <div className="lg:col-span-8">
              <MilestonesTimeline />
            </div>
          </div>
        </div>
      </section>

      {/* Vizyon & Misyon */}
      <section id="vizyon" className="bg-cream-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28 grid md:grid-cols-3 gap-6">
          {[
            { icon: Compass, kicker: 'Misyonumuz', title: 'Her çocuğa eşit bir başlangıç', text: 'Maddi imkânları yeterli olmayan başarılı öğrencilere karşılıksız eğitim bursu sağlamak, sosyal ve kültürel gelişimlerini desteklemek.' },
            { icon: Eye, kicker: 'Vizyonumuz', title: 'Türkiye\'nin en güvenilir eğitim vakfı', text: 'Eğitimde fırsat eşitliği yaratmak için ulusal referans olmak; yenilikçi, şeffaf ve sürdürülebilir bir vakıf.' },
            { icon: Target, kicker: 'Değerlerimiz', title: 'Şeffaflık, liyakat ve dayanışma', text: 'Bağışçılarımıza şeffaf hesap verebilirlik; bursiyerlerimize liyakat esaslı destek; aramızda dayanışma.' },
          ].map((b, i) => {
            const Icon = b.icon;
            const n = i + 1;
            return (
              <div key={i} className="bg-white rounded-3xl p-8 border border-ink-100">
                <div className="w-12 h-12 rounded-xl bg-ink-50 grid place-items-center text-ink-600"><Icon className="w-5 h-5" /></div>
                <InlineText settingKey={`about_pillar_${n}_kicker`} fallback={b.kicker} as="div"
                  className="text-[10px] uppercase tracking-widest mt-5 text-coral-300 font-semibold" />
                <InlineText settingKey={`about_pillar_${n}_title`} fallback={b.title} as="h3"
                  multiline className="font-display text-2xl mt-2 text-ink-600 leading-snug" />
                <InlineText settingKey={`about_pillar_${n}_text`} fallback={b.text} as="p"
                  multiline className="mt-3 text-ink-500 text-sm leading-relaxed" />
              </div>
            );
          })}
        </div>
      </section>

      {/* Başkanın Mesajı — Real chairman: Ş. Tülay ERKAN */}
      <section id="baskan" className="relative bg-ink-600 text-cream overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 15% 20%, #C9A96E 0, transparent 50%), radial-gradient(circle at 85% 80%, #E8826F 0, transparent 50%)' }} />
        <div className="absolute -right-40 -top-40 w-[500px] h-[500px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Photo with editorial styling */}
            <div className="lg:col-span-5 relative" data-testid="chairman-photo-wrap">
              <div className="relative">
                {/* Frame offset */}
                <div className="absolute -inset-3 rounded-[36px] border border-gold/30 translate-x-4 translate-y-4 pointer-events-none" />
                <div className="relative rounded-[32px] overflow-hidden aspect-[4/5] bg-ink-700 shadow-2xl shadow-ink-900/50">
                  <img src={CHAIRMAN.image} alt={CHAIRMAN.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-900/85 via-ink-900/20 to-transparent" />

                  {/* Gold badge */}
                  <div className="absolute top-6 left-6 inline-flex items-center gap-1.5 bg-gold text-ink-700 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em]">
                    <Crown className="w-3.5 h-3.5" /> Başkan
                  </div>

                  {/* Bottom caption */}
                  <div className="absolute bottom-0 left-0 right-0 p-7">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold mb-2">Mütevelli Heyet & Yönetim Kurulu Başkanı</div>
                    <div className="font-display text-3xl md:text-4xl leading-tight">{CHAIRMAN.name}</div>
                  </div>
                </div>

                {/* Floating years badge */}
                <div className="hidden md:flex absolute -left-6 -bottom-24 lg:-bottom-28 items-center gap-3 bg-cream text-ink-600 rounded-2xl shadow-2xl p-5 border border-gold/30 whitespace-nowrap">
                  <div className="w-12 h-12 rounded-xl bg-gold/20 text-gold-500 grid place-items-center shrink-0"><Star className="w-5 h-5" /></div>
                  <div>
                    <InlineText settingKey="about_chairman_badge_label" fallback="Kurumsal miras" as="div"
                      className="text-[10px] uppercase tracking-widest text-ink-400" />
                    <InlineText settingKey="about_chairman_badge_value" fallback="33 Yıl" as="div"
                      className="font-display text-2xl tabular leading-none mt-1" />
                  </div>
                </div>
              </div>
            </div>

            {/* Message */}
            <div className="lg:col-span-7">
              <InlineText settingKey="about_chairman_kicker" fallback="Başkanın Mesajı" as="div"
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-gold font-semibold" />
              <InlineText settingKey="about_chairman_title" fallback={'Yeni bir dönem, <span class="italic text-coral-200">yeni ufuklar</span>.'} as="h2"
                html multiline className="font-display text-4xl md:text-5xl lg:text-[56px] mt-5 leading-[1.02]" />

              <Quote className="w-10 h-10 text-gold mt-8" />
              <InlineText settingKey="about_chairman_message" fallback={CHAIRMAN.message} as="p"
                multiline className="mt-4 text-cream/80 text-base md:text-lg leading-relaxed" />

              {/* Motto highlight */}
              <div className="mt-8 relative pl-6 border-l-2 border-gold">
                <InlineText settingKey="about_chairman_motto_kicker" fallback="Misyonumuz" as="div"
                  className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2 font-semibold" />
                <InlineText settingKey="about_chairman_motto" fallback={CHAIRMAN.motto} as="p"
                  multiline className="font-display text-xl md:text-2xl italic leading-snug text-cream before:content-['“'] after:content-['”']" />
              </div>

              <div className="mt-10 flex items-center gap-6">
                <div>
                  <InlineText settingKey="about_chairman_name" fallback={CHAIRMAN.name} as="div"
                    className="font-display text-2xl" />
                  <InlineText settingKey="about_chairman_role" fallback={CHAIRMAN.role} as="div"
                    className="text-cream/60 text-sm mt-0.5" />
                </div>
                <div className="h-10 w-px bg-cream/20" />
                <a href="/kurumsal/baskanlar" className="inline-flex items-center gap-2 text-gold hover:gap-3 transition-all text-sm font-semibold">
                  Tüm başkanlarımız <ChevronRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Yönetim Kurulu — Real board with editorial grid */}
      <section id="yonetim" className="relative bg-cream-200 overflow-hidden">
        {/* Big typographic backdrop */}
        <div className="absolute inset-0 pointer-events-none select-none opacity-[0.04]" aria-hidden="true">
          <div className="absolute top-20 -right-10 font-display text-[220px] md:text-[320px] leading-none text-ink-600 font-bold tracking-tighter">2026</div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
          {/* Section header */}
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <InlineText settingKey="about_board_kicker" fallback="Yönetim Kurulumuz" as="div"
                className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold" />
              <InlineText settingKey="about_board_title" fallback={'Arkamızda <span class="italic text-coral-300">güçlü</span> bir kadro.'} as="h2"
                html multiline className="font-display text-4xl md:text-5xl lg:text-[64px] mt-4 leading-[1.0] text-ink-600" />
            </div>
            <div className="lg:col-span-5">
              <InlineText settingKey="about_board_text" fallback="Mütevelli Heyeti'miz tarafından seçilen 11 kişilik yönetim kurulumuz; vakfımızın kararlarını şeffaflık, liyakat ve dayanışma ilkeleri çerçevesinde alır." as="p"
                multiline className="text-ink-500 leading-relaxed" />
            </div>
          </div>

          {/* Chairman spotlight card — top of grid */}
          <div className="mt-14" data-testid="chairman-card">
            <div className="group relative grid md:grid-cols-12 gap-0 bg-ink-600 text-cream rounded-[28px] overflow-hidden shadow-xl">
              <div className="md:col-span-4 relative aspect-[4/5] md:aspect-auto bg-ink-700 overflow-hidden">
                <img src={CHAIRMAN.image} alt={CHAIRMAN.name} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-r from-ink-900/40 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-ink-600/80" />
              </div>
              <div className="md:col-span-8 p-8 md:p-12 lg:p-14 flex flex-col justify-center relative">
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">01 · Başkan</div>
                <h3 className="font-display text-4xl md:text-5xl mt-3 leading-[1.05]">{CHAIRMAN.name}</h3>
                <div className="mt-2 text-cream/75">{CHAIRMAN.role}</div>
                <div className="mt-6 h-px w-16 bg-gold" />
                <InlineText settingKey="about_chairman_card_motto" fallback={`“${CHAIRMAN.motto}”`} as="p"
                  multiline className="mt-6 text-cream/70 italic text-lg leading-relaxed max-w-xl" />
              </div>
            </div>
          </div>

          {/* Executive team — İcra Heyeti */}
          <div className="mt-16">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <InlineText settingKey="about_executive_kicker" fallback="İcra Heyeti" as="div"
                  className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold" />
                <InlineText settingKey="about_executive_title" fallback="Başkan Yardımcıları & Görevliler" as="h3"
                  multiline className="font-display text-3xl md:text-4xl text-ink-600 mt-2 leading-tight" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-ink-400 font-semibold tabular">02 — 05</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {EXECUTIVE.map((m, i) => {
                const n = i + 1;
                return (
                <EditRegion key={m.name} label="İcra Heyeti Üyesi" to="/admin/board">
                <article
                  className="group relative bg-white rounded-3xl p-7 border border-ink-100 hover:border-gold transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-ink-900/10"
                  data-testid={`executive-member-${i}`}
                >
                  {/* Index */}
                  <div className="absolute top-5 right-5 font-display text-[13px] text-ink-300 tabular">{String(i + 2).padStart(2, '0')}</div>

                  {/* Monogram avatar */}
                  <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-ink-600 to-ink-700 grid place-items-center text-cream">
                    <span className="font-display text-xl tracking-wider">{initials(m.name)}</span>
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-gold grid place-items-center">
                      <Star className="w-2.5 h-2.5 text-ink-700" fill="currentColor" />
                    </div>
                  </div>

                  <InlineText settingKey={`about_executive_${n}_short`} fallback={m.short} as="div"
                    className="mt-6 text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold" />
                  <InlineText settingKey={`about_executive_${n}_name`} fallback={m.name} as="h4"
                    className="mt-2 font-display text-xl text-ink-600 leading-tight" />
                  <InlineText settingKey={`about_executive_${n}_role`} fallback={m.role} as="div"
                    className="mt-1.5 text-sm text-ink-400" />

                  <div className="mt-6 h-px bg-ink-100 group-hover:bg-gold/40 transition-colors" />
                  <div className="mt-4 text-[10px] tracking-[0.25em] uppercase text-ink-400 font-semibold">Yönetim Kurulu</div>
                </article>
                </EditRegion>
                );
              })}
            </div>
          </div>

          {/* Members — Üyeler */}
          <div className="mt-20">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <InlineText settingKey="about_members_kicker" fallback="Üyelerimiz" as="div"
                  className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold" />
                <InlineText settingKey="about_members_title" fallback="Yönetim Kurulu Üyeleri" as="h3"
                  multiline className="font-display text-3xl md:text-4xl text-ink-600 mt-2 leading-tight" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-ink-400 font-semibold tabular">06 — 11</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {MEMBERS.map((m, i) => (
                <article
                  key={m.name}
                  className="group relative bg-white rounded-3xl p-7 border border-ink-100 hover:border-gold transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-ink-900/10"
                  data-testid={`board-member-${i}`}
                >
                  {/* Index */}
                  <div className="absolute top-5 right-5 font-display text-[13px] text-ink-300 tabular">{String(i + 6).padStart(2, '0')}</div>

                  {/* Monogram avatar */}
                  <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-ink-600 to-ink-700 grid place-items-center text-cream">
                    <span className="font-display text-xl tracking-wider">{initials(m.name)}</span>
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-gold grid place-items-center">
                      <Star className="w-2.5 h-2.5 text-ink-700" fill="currentColor" />
                    </div>
                  </div>

                  <InlineText settingKey={`about_member_${i + 1}_role`} fallback={m.role} as="div"
                    className="mt-6 text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold" />
                  <InlineText settingKey={`about_member_${i + 1}_name`} fallback={m.name} as="h4"
                    className="mt-2 font-display text-xl text-ink-600 leading-tight" />

                  <div className="mt-6 h-px bg-ink-100 group-hover:bg-gold/40 transition-colors" />
                  <div className="mt-4 text-[10px] tracking-[0.25em] uppercase text-ink-400 font-semibold">Yönetim Kurulu</div>
                </article>
              ))}
            </div>
          </div>

          {/* Bottom footer line */}
          <div className="mt-14 flex flex-wrap items-center justify-between gap-6 pt-8 border-t border-ink-100">
            <div className="text-sm text-ink-400">
              Kurulumuz, vakfımızın stratejik kararlarını <span className="text-ink-600 font-semibold">şeffaflık</span> ve <span className="text-ink-600 font-semibold">liyakat</span> ilkeleri çerçevesinde alır.
            </div>
            <a href="/iletisim" className="inline-flex items-center gap-2 text-ink-600 font-semibold text-sm hover:gap-3 transition-all" data-testid="board-contact-link">
              Kurulumuza ulaşın <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </>
    </EditRegion>
  );
}

function MilestonesTimeline() {
  const [items, setItems] = useState(null); // null = loading, [] = empty, [...] = ready
  useEffect(() => {
    api.publicContent('milestones')
      .then(d => setItems(Array.isArray(d) ? d : []))
      .catch(() => setItems([]));
  }, []);

  if (items === null) {
    return (
      <div className="relative border-l-2 border-ink-100 pl-8 space-y-10">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="space-y-2">
            <div className="h-8 w-24 bg-ink-50 rounded animate-pulse" />
            <div className="h-3 w-40 bg-ink-50 rounded animate-pulse" />
            <div className="h-4 w-full bg-ink-50 rounded animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  // Fallback (only used when DB is completely empty — normally seed will populate)
  const list = items.length > 0
    ? items.map(x => ({ id: x.id, year: x.data?.year, title: x.data?.title, text: x.data?.text }))
    : TIMELINE.map((t, i) => ({ id: `fallback-${i}`, ...t }));

  return (
    <div className="relative border-l-2 border-ink-100 pl-8 space-y-10">
      {list.map(t => (
        <EditRegion key={t.id} label="Bu Kilometre Taşını Düzenle" to="/admin/milestones">
          <div className="relative">
            <div className="absolute -left-[42px] top-1 w-4 h-4 rounded-full bg-gold ring-4 ring-cream" />
            <div className="font-display text-3xl text-ink-600 tabular">{t.year}</div>
            {t.title && <div className="text-coral-300 text-xs uppercase tracking-widest mt-1 font-semibold">{t.title}</div>}
            {t.text && <p className="mt-2 text-ink-500 whitespace-pre-line">{t.text}</p>}
          </div>
        </EditRegion>
      ))}
    </div>
  );
}

